import { useState, type JSX } from "react";
import { DocumentAddRegular, ClockRegular, OpenRegular, ChevronRightRegular } from "@fluentui/react-icons";
import { TabSectionHeader } from "@/components/Workflow/TabSectionHeader";
import { getCreateDealDisplay, openCreateDealDialog } from "@/services/outlook/create-deal-dialog";
import { getWeeklyHoursDisplay, openWeeklyHoursDialog } from "@/services/outlook/weekly-hours-dialog";
import { getWorkflowAccess } from "@/services/auth/role-privileges";
import { useAuth } from "@/services/auth/use-auth";
import { resolveLaunchUrl } from "@/utils/launch-url";
import { CreateDeal } from "./CreateDeal";
import { WeeklyHours } from "./WeeklyHours";
import "../shared.scss";
import "./QuickActions.scss";

interface QuickAction {
  id: string;
  title: string;
  desc: string;
  icon: JSX.Element;
  action: "create-deal" | "weekly-hours" | "launch-web";
}

const ACTIONS: QuickAction[] = [
  {
    id: "create-deal",
    title: "Create Deal",
    desc: "Set up a new Deal without leaving Outlook",
    icon: <DocumentAddRegular />,
    action: "create-deal",
  },
  {
    id: "weekly-hours",
    title: "Enter Weekly Hours",
    desc: "Open the TRIYO form for your eligible Deals",
    icon: <ClockRegular />,
    action: "weekly-hours",
  },
  {
    id: "launch-triyo",
    title: "Launch TRIYO",
    desc: "Open the full TRIYO web application",
    icon: <OpenRegular />,
    action: "launch-web",
  },
];

export function QuickActions(): JSX.Element {
  const { user } = useAuth();
  const [view, setView] = useState<"list" | "create-deal" | "weekly-hours">("list");
  const [notice, setNotice] = useState("");
  const { canCreateDeals } = getWorkflowAccess(user);
  const actions = canCreateDeals ? ACTIONS : ACTIONS.filter((item) => item.action !== "create-deal");

  const openAction = (item: QuickAction) => {
    setNotice("");
    if (item.action === "create-deal") {
      if (getCreateDealDisplay() === "dialog") {
        void openCreateDealDialog().then((result) => {
          if (result === "unavailable") setView("create-deal");
        });
        return;
      }
      setView("create-deal");
      return;
    }
    if (item.action === "weekly-hours") {
      if (getWeeklyHoursDisplay() === "dialog") {
        void openWeeklyHoursDialog().then((result) => {
          if (result === "unavailable") setView("weekly-hours");
        });
        return;
      }
      setView("weekly-hours");
      return;
    }
    const launchUrl = resolveLaunchUrl();
    if (launchUrl) {
      window.open(launchUrl, "_blank", "noopener,noreferrer");
    } else {
      setNotice("The TRIYO web application URL is not configured.");
    }
  };

  if (view !== "list") {
    return (
      <div className="tab-panel">
        <TabSectionHeader
          title={view === "create-deal" ? "Create Deal" : "Enter Weekly Hours"}
          action={
            <button type="button" className="quick-actions__back" onClick={() => setView("list")}>
              Back
            </button>
          }
        />
        {view === "create-deal" ? (
          <CreateDeal onClose={() => setView("list")} />
        ) : (
          <div className="list-scroll quick-actions__detail">
            <WeeklyHours />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <TabSectionHeader title="Quick Actions" />
      {notice ? (
        <div className="quick-actions__notice" role="status" aria-live="polite">
          {notice}
        </div>
      ) : null}
      <div className="list-scroll quick-actions__list">
        {actions.map((item) => (
          <button key={item.id} type="button" className="quick-action-row" onClick={() => openAction(item)}>
            <span className="quick-action-row__body">
              <span className="quick-action-row__left">
                <span className="quick-action-row__icon">{item.icon}</span>
                <span className="quick-action-row__text">
                  <span className="quick-action-row__title">{item.title}</span>
                  <span className="quick-action-row__desc">{item.desc}</span>
                </span>
              </span>
              <ChevronRightRegular className="quick-action-row__chevron" />
            </span>
            <span className="list-row__divider" />
          </button>
        ))}
      </div>
    </div>
  );
}
